import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X } from "lucide-react";
import { useSite, waLinkFromNumber, formatPhone } from "@/context/SiteContext";

export default function FloatingWhatsApp() {
  const { content } = useSite();
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 200);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const waUrl = waLinkFromNumber(
    content.whatsapp,
    "Hi KOSTRA Hospitality — I'd like to learn more about your B2B food solutions.",
  );

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.9 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-24 right-6 z-[150] flex flex-col items-end gap-3"
          data-testid="floating-whatsapp"
        >
          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="organic-card !p-0 overflow-hidden w-[300px] mr-1"
              >
                <div className="bg-[var(--k-green)] px-5 py-4 flex items-center justify-between">
                  <div>
                    <div className="text-[var(--k-bg)] font-serif-display text-lg leading-none">
                      KOSTRA Hospitality
                    </div>
                    <div className="font-mono-label text-[var(--k-olive-soft)] text-[0.55rem] mt-1.5">
                      Typically replies in 5 minutes
                    </div>
                  </div>
                  <button
                    onClick={() => setOpen(false)}
                    className="text-[var(--k-bg)] opacity-70 hover:opacity-100 transition-opacity"
                    data-testid="whatsapp-close-btn"
                    aria-label="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-5">
                  <div className="bg-[var(--k-bg-2)] rounded-xl rounded-tl-none p-3 text-[var(--k-text)] text-sm leading-relaxed">
                    Hi! 👋 Looking for product development, consultancy or raw
                    material supply? Let's chat on WhatsApp.
                  </div>
                  <a
                    href={waUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="whatsapp-chat-btn"
                    className="mt-4 flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1ebd5a] text-white py-3 rounded-full font-mono-label tracking-wider text-[0.7rem] transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" strokeWidth={2} />
                    Start Chat
                  </a>
                  <div
                    className="text-center font-mono-label text-[var(--k-muted)] text-[0.55rem] mt-3"
                    data-testid="whatsapp-number-display"
                  >
                    {formatPhone(content.whatsapp)}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <button
            onClick={() => setOpen((p) => !p)}
            data-testid="whatsapp-fab"
            className="relative w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1ebd5a] text-white shadow-lg shadow-[rgba(37,211,102,0.35)] flex items-center justify-center transition-all hover:scale-105"
            aria-label="WhatsApp KOSTRA"
          >
            <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" />
            <MessageCircle className="w-6 h-6 relative" strokeWidth={1.8} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
